import { useEffect, useState } from 'react';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, dateTime } from '@/lib/format';
import { Page } from '@/components/page';
import { Alert, Button, Card, CardHeader, Pill, Skeleton } from '@/components/ui';
import { CodeForm, RecoveryCodes, ScanCode } from '@/components/two-factor';
import { useSecurityStore } from '@/stores/security.store';

/**
 * Sign-in security (ADR-116): each person's own two-step sign-in — on with an
 * authenticator app, recovery codes renewed, off where the store allows it.
 */
export function SecurityPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { status, setup, codes, loading, submitting, error, load, startSetup, cancelSetup, enable, renewCodes, disable, dismissCodes } = useSecurityStore();
  const [action, setAction] = useState<'renew' | 'disable' | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  const policyLine = status ? t(status.policy === 'required' ? 'twoFactorPolicyRequired' : status.policy === 'off' ? 'twoFactorPolicyOff' : 'twoFactorPolicyOptional') : '';

  return (
    <Page title={t('securityTitle')} subtitle={t('securityHint')}>
      <Card className="max-w-xl">
        <CardHeader
          title={t('twoFactorTitle')}
          subtitle={policyLine}
          action={status ? <Pill tone={status.enabled ? 'success' : 'neutral'}>{t(status.enabled ? 'twoFactorOn' : 'twoFactorOff')}</Pill> : null}
        />
        <div className="flex flex-col gap-4 px-5 pb-5">
          {loading && !status ? <Skeleton className="h-24" /> : null}
          {status && !status.available ? <Alert tone="info">{t('twoFactorUnavailable')}</Alert> : null}

          {codes ? (
            <>
              <RecoveryCodes codes={codes} />
              <Button onClick={dismissCodes}>{t('twoFactorCodesSaved')}</Button>
            </>
          ) : status && !status.enabled ? (
            setup ? (
              <>
                <ScanCode setup={setup} />
                <CodeForm submit={enable} submitting={submitting} label={t('twoFactorFirstCode')} action={t('twoFactorTurnOn')} />
                <Button variant="ghost" onClick={cancelSetup}>
                  {t('cancel')}
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-ink-muted">{t('twoFactorWhy')}</p>
                {status.policy !== 'off' && status.available ? (
                  <Button className="self-start" onClick={() => void startSetup()} disabled={submitting}>
                    {t('twoFactorStart')}
                  </Button>
                ) : null}
              </>
            )
          ) : status ? (
            <>
              <p className="text-sm text-ink-muted">
                {t('twoFactorOnSince', { date: status.enabledAt ? dateTime(status.enabledAt, locale) : '' })} ·{' '}
                {t('twoFactorCodesLeft', { count: count(status.recoveryCodesLeft, locale) })}
              </p>
              {action ? (
                <>
                  <CodeForm
                    submit={async (code) => {
                      await (action === 'renew' ? renewCodes(code) : disable(code));
                      if (!useSecurityStore.getState().error) setAction(null);
                    }}
                    submitting={submitting}
                    label={t('twoFactorCode')}
                    action={t(action === 'renew' ? 'twoFactorRenewCodes' : 'twoFactorTurnOff')}
                    recovery={action === 'disable'}
                  />
                  <Button variant="ghost" onClick={() => setAction(null)}>
                    {t('cancel')}
                  </Button>
                </>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={() => setAction('renew')}>
                    {t('twoFactorRenewCodes')}
                  </Button>
                  {status.policy !== 'required' ? (
                    <Button variant="ghost" onClick={() => setAction('disable')}>
                      {t('twoFactorTurnOff')}
                    </Button>
                  ) : null}
                </div>
              )}
            </>
          ) : null}

          {error ? <Alert>{error}</Alert> : null}
        </div>
      </Card>
    </Page>
  );
}
